import React, { useState } from 'react';
import { Upload, Download, RefreshCw, CheckCircle, XCircle } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card';

interface Zone {
  id: string;
  name: string;
  type: 'text' | 'table' | 'checkbox' | 'date';
  coordinates: {
    x: number;
    y: number;
    width: number;
    height: number;
  };
  rules: ExtractionRule[];
  required: boolean;
}

interface ExtractionRule {
  id: string;
  type: 'regex' | 'keyword' | 'position' | 'table';
  value: string;
  config: Record<string, any>;
}

interface ExtractionResult {
  zoneId: string;
  zoneName: string;
  value: string | string[][] | null;
  confidence: number;
  success: boolean;
  error?: string;
}

interface TemplatePreviewProps {
  zones: Zone[];
}

const TemplatePreview: React.FC<TemplatePreviewProps> = ({ zones }) => {
  const [testDocument, setTestDocument] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState<ExtractionResult[]>([]);

  const handleDocumentUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setTestDocument(e.target?.result as string);
        runExtraction();
      };
      reader.readAsDataURL(file);
    }
  };

  const runExtraction = async () => {
    setLoading(true);
    // Simulate extraction process
    const mockResults: ExtractionResult[] = zones.map(zone => ({
      zoneId: zone.id,
      zoneName: zone.name,
      value: zone.type === 'table' ? [['Sample', 'Data'], ['Row 1', 'Value 1']] : 'Sample extracted text',
      confidence: Math.random() * 0.5 + 0.5, // Random confidence between 50-100%
      success: Math.random() > 0.2, // 80% success rate for testing
      error: Math.random() > 0.8 ? 'Failed to extract data from zone' : undefined
    }));

    // Simulate API delay
    await new Promise(resolve => setTimeout(resolve, 1000));
    setResults(mockResults);
    setLoading(false);
  };

  const handleDownloadResults = () => {
    const data = {
      timestamp: new Date().toISOString(),
      results: results
    };
    
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'template-test-results.json';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="h-full flex flex-col">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-4">
          <label className="flex items-center gap-2 px-4 py-2 bg-purple-500/20 hover:bg-purple-500/30 rounded-lg text-purple-300 cursor-pointer">
            <Upload className="w-4 h-4" />
            Upload Test Document
            <input
              type="file"
              accept="image/*,.pdf"
              onChange={handleDocumentUpload}
              className="hidden"
            />
          </label>
          {results.length > 0 && (
            <>
              <button
                onClick={runExtraction}
                className="flex items-center gap-2 px-4 py-2 bg-purple-500/20 hover:bg-purple-500/30 rounded-lg text-purple-300"
              >
                <RefreshCw className="w-4 h-4" />
                Run Again
              </button>
              <button
                onClick={handleDownloadResults}
                className="flex items-center gap-2 px-4 py-2 bg-purple-500/20 hover:bg-purple-500/30 rounded-lg text-purple-300"
              >
                <Download className="w-4 h-4" />
                Download Results
              </button>
            </>
          )}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto">
        {loading ? (
          <div className="flex items-center justify-center h-full">
            <div className="flex flex-col items-center gap-4">
              <div className="w-8 h-8 border-4 border-purple-500/20 border-t-purple-500 rounded-full animate-spin" />
              <p className="text-purple-300">Processing document...</p>
            </div>
          </div>
        ) : testDocument ? (
          <div className="grid grid-cols-2 gap-4">
            {/* Document Preview */}
            <div>
              <h3 className="text-sm font-medium text-purple-400 mb-2">Document</h3>
              <div className="relative border border-purple-500/20 rounded-lg overflow-hidden">
                <img
                  src={testDocument}
                  alt="Test Document"
                  className="max-w-full h-auto"
                />
                {/* Highlight extracted zones */}
                {results.map((result) => {
                  const zone = zones.find(z => z.id === result.zoneId);
                  if (!zone) return null;
                  
                  return (
                    <div
                      key={zone.id}
                      className={`absolute border-2 ${
                        result.success
                          ? 'border-green-500/50 bg-green-500/10'
                          : 'border-red-500/50 bg-red-500/10'
                      }`}
                      style={{
                        left: `${zone.coordinates.x}px`,
                        top: `${zone.coordinates.y}px`,
                        width: `${zone.coordinates.width}px`,
                        height: `${zone.coordinates.height}px`
                      }}
                    />
                  );
                })}
              </div>
            </div>

            {/* Extraction Results */}
            <div>
              <h3 className="text-sm font-medium text-purple-400 mb-2">Results</h3>
              <div className="space-y-4">
                {results.map((result) => (
                  <Card key={result.zoneId} className="bg-purple-500/5">
                    <CardHeader className="p-4 pb-2">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          {result.success ? (
                            <CheckCircle className="w-4 h-4 text-green-400" />
                          ) : (
                            <XCircle className="w-4 h-4 text-red-400" />
                          )}
                          <CardTitle className="text-sm font-medium text-purple-100">
                            {result.zoneName}
                          </CardTitle>
                        </div>
                        <span className="text-xs text-purple-400">
                          {(result.confidence * 100).toFixed(1)}% confidence
                        </span>
                      </div>
                    </CardHeader>
                    <CardContent className="p-4 pt-2">
                      {result.error ? (
                        <p className="text-sm text-red-400">{result.error}</p>
                      ) : Array.isArray(result.value) ? (
                        <div className="overflow-x-auto">
                          <table className="min-w-full divide-y divide-purple-500/20">
                            <tbody className="divide-y divide-purple-500/20">
                              {(result.value as string[][]).map((row, i) => (
                                <tr key={i}>
                                  {row.map((cell, j) => (
                                    <td
                                      key={j}
                                      className="px-3 py-2 text-sm text-purple-100"
                                    >
                                      {cell}
                                    </td>
                                  ))}
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      ) : (
                        <p className="text-sm text-purple-100">{result.value}</p>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <Upload className="w-12 h-12 text-purple-400/40 mx-auto mb-4" />
              <p className="text-purple-400/60">
                Upload a document to test template extraction
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TemplatePreview;